import DynamicFormInput from "components/form/form";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useEffect, useState, useCallback } from "react";
import { LoadingIcon } from "utils/constant";
import personImg from "../../images/person.png";
import SingleLawImplementationFollowup from "./fragments/singleLawImplementationFollowup";
import SingleLawImplementationFollowupDetail from "./fragments/singleLawImplementationFollowupDetail";
import FileViewer from "components/file/fileViewContainer";
import Pagination from "components/Pagination";
import { useTranslation } from "react-i18next";

const fetchPublicLawImplementations = async () => {
  
  const baseUrl = process.env.REACT_APP_API_BASE_URL || "https://test.research-api.oagb.et/api/";
  const response = await fetch(
    `${baseUrl}law_implementation_follow_up/mapped-summary/`,
    {
      method: "GET",
    }
  );
  if (!response.ok) {
    throw new Error("Failed to fetch public law implementations");
  }
  const data = await response.json();

  return data.map((item: any, index: number) => ({
    id: item.id || `impl-${index}`,
    registry_number: item.registry_number || "",
    identifies_specific_laws_government_sectors: item.identifies_specific_laws_government_sectors || "",
    description: item.description || "",
    enforcement_analysis_summary: item.enforcement_analysis_summary || "",
    audit_findings: item.audit_findings || "",
    is_published: Boolean(item.is_published),
    updated_at: item.updated_at || null,
    created_at: item.created_at || null,
    prosecutor_assignments: item.prosecutor_assignments || [],
    documents: (item.attached_documents || []).map((doc: any) => ({
      uu_id: doc.uu_id || doc.id || "",
      document_uuid: doc.document_uuid || doc.uuid || "",
      document_type: doc.document_type || "",
      document_path: doc.document_path || "",
      ...doc
    })),
  }));
};

export default function PublicLawImplementation() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeImplementation, setImplementation] = useState<any>();
  const [submitting, setSubmitting] = useState(false);
  const { token } = useAuth();
  const { showToast } = useToast();
  const [data, setData] = useState<{
    isLoading: boolean;
    data: any[];
  }>({ isLoading: true, data: [] });

  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 2;

  const fileRowProps = {
    fileViewerNumber: 2,
    setFileViewer2: setIsFileViewerOpen,
    setSelectedApplicantDocument: (document: any) => {
      setSelectedDocument(document);
      setIsFileViewerOpen(true);
    },
  };

  const filteredData = useCallback(() => {
    if (!searchTerm.trim()) {
      return data.data || [];
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return (data.data || []).filter((req: any) => {
      return (
        (req.registry_number || "").toLowerCase().includes(searchLower) ||
        (req.identifies_specific_laws_government_sectors || "").toLowerCase().includes(searchLower) ||
        (req.description || "").toLowerCase().includes(searchLower)
      );
    });
  }, [data.data, searchTerm]);

  const paginatedData = useCallback(() => {
    const filtered = filteredData();
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filtered.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredData().length / pageSize);

  useEffect(() => {
    const currentPaginated = paginatedData();
    if (currentPaginated.length > 0) {
      const currentActiveInPaginated = currentPaginated.find(
        item => item.id === activeImplementation?.id
      );

      if (currentActiveInPaginated) {
        setImplementation(currentActiveInPaginated);
      } else {
        setImplementation(currentPaginated[0]);
      }
    } else {
      setImplementation(undefined);
    }
  }, [paginatedData, activeImplementation?.id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

   const handleSearch = async () => {
      setSubmitting(true);
      try {
        // Frontend only search - filtering happens via memoized callbacks
        await new Promise((resolve) => setTimeout(resolve, 400));
      } catch (error) {
        console.error("Search error:", error);
        showToast("Search failed", "error");
      } finally {
        setSubmitting(false);
      }
    };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setData({ isLoading: true, data: [] });

        const implementations = await fetchPublicLawImplementations();

        const publishedItems = implementations.filter(
          (req: any) => req.is_published === true
        );

        setData({ isLoading: false, data: publishedItems });

        if (publishedItems.length > 0) {
          setImplementation(publishedItems[0]);
        }
        setCurrentPage(1);
      } catch (error) {
        console.error("Error fetching public Law Implementation:", error);
        showToast("Failed to fetch public law implementation data.", "error");
        setData(prev => ({ ...prev, isLoading: false }));
      }
    };

    fetchData();
  }, [showToast]);

  const currentFilteredResults = filteredData();
  const currentPaginatedResults = paginatedData();
  const hasFilteredResults = currentFilteredResults.length > 0;

  return (
    <div className="flex flex-col md:flex-row h-full gap-4">
      <div className="flex flex-col flex-1 overflow-y-hidden">
        <div className="flex flex-col bg-white rounded-lg shadow-md p-4 md:p-6 mb-4">
          <p className="mb-2 font-medium">{t("SearchPublishedLawImplementations")}</p>
          <div className="flex flex-col md:flex-row gap-4">
             <div className="w-full">
               <DynamicFormInput
                 inputs={[{
                   value: searchTerm,
                   type: "text",
                   label: "Search by reference number,...",
                   name: "search",
                   onChange: handleChange,
                   placeholder: "Eg. 12345,...",
                 }]}
               />
            </div>
            <button
              onClick={handleSearch}
              disabled={submitting}
              className="bg-[#212121] hover:bg-[#141414] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-2 px-12 rounded-md h-fit w-full md:w-fit"
            >
              {submitting ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon} Searching...
                </div>
              ) : (
                "Search"
              )}
            </button>
          </div>
          {searchTerm && (
            <div className="mt-2 text-sm text-gray-600">
              Found {currentFilteredResults.length} result(s) for "{searchTerm}"
            </div>
          )}
        </div>

        {data.isLoading ? (
           <div className="text-center mb-auto text-gray-500 py-8">
             <div className="flex justify-center mb-2">
               {LoadingIcon}
             </div>
             <p>Loading translation data...</p>
           </div>
        ) : !hasFilteredResults ? (
           <div className="text-center mb-auto text-gray-500 py-8">
             <p>
               {searchTerm.trim()
                 ? `No results found for "${searchTerm}"`
                 : "No legal translation papers have been submitted yet"}
             </p>
           </div>
        ) : (
          <>
            <div className="flex flex-col gap-4 overflow-y-scroll scrollbar-hide flex-1">
              {currentPaginatedResults.map((impl: any) => (
                <SingleLawImplementationFollowup
                  key={impl.id}
                  personImg={personImg}
                  implementation={impl}
                  isActive={impl.id === activeImplementation?.id}
                  onClick={() => setImplementation(impl)}
                />
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>

      {/* Detail Panel */}
      <div className="flex flex-col flex-1 h-full overflow-scroll scrollbar-hide">
        {hasFilteredResults && activeImplementation ? (
          <SingleLawImplementationFollowupDetail
            implementation={activeImplementation}
            personImg={personImg}
            fileRowProps={fileRowProps}
          />
         ) : hasFilteredResults && !activeImplementation ? (
           <div className="text-center p-8 text-gray-500">
             <p>Select a translation paper to view details</p>
           </div>
        ) : null}
      </div>
      <FileViewer
        selectedApplicantDocument={selectedDocument || undefined}
        isPopupOpen={isFileViewerOpen}
        setIsPopupOpen={setIsFileViewerOpen}
      />
    </div>
  );
}
